import { getCurrentAuthToken } from 'utils/authHelpers';
import { getLocalAppSession } from 'utils/storageHelpers';
import { leaveSignedOut } from 'utils/signedOut';
import { BasedOn, refusedOrVersion, withBasedOn } from 'api/keptVersion';

/**
 * A candidate's note stores itself (auto-save, D4: it keeps plainly, nothing
 * waits for a publish). One text, one store.
 */
export const notePath = (procedureId: number): string =>
  `/v4/procedures/${procedureId}`;

export const noteBody = (notes: string): Record<string, unknown> => ({
  // An emptied note is stored as "no note".
  notes: notes === '' ? null : notes,
});

/** Stores the note, quietly. @throws when the server did not confirm */
export const saveCandidateNote = async (
  procedureId: number,
  notes: string,
  signal?: AbortSignal,
  basedOn?: BasedOn
): Promise<number | null> => {
  const base = (import.meta.env.VITE_BASE_URL_API as string) ?? '';
  const response = await fetch(`${base}${notePath(procedureId)}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${getCurrentAuthToken('preserve')}`,
      'Active-Customer': String(getLocalAppSession()?.activeCustomer ?? ''),
    },
    body: JSON.stringify(withBasedOn(noteBody(notes), basedOn)),
    signal,
  });
  if (response.status === 401) {
    if (window.location.pathname !== '/') leaveSignedOut();
    throw new Error('The session has ended.');
  }
  // D10: out of date is not a failure; the person chooses.
  const version = await refusedOrVersion(response);
  if (!response.ok) {
    throw new Error(`The note was not stored (${response.status}).`);
  }
  const answer = (await response.json().catch(() => ({}))) as {
    success?: boolean;
  };
  if (answer.success !== true) throw new Error('The note was not stored.');
  return version;
};
